import type { RequestStatus } from '@features/request/requestSlice';
import { useResetStoreData } from '@features/request/useResetStoreData';
import type { Meta } from '@utils/types';
import { useRequest } from 'features/request/useRequest';
import type {
    BimcCalculationLimitedCostConstructionTypeInput,
    BimcUpdateCalculationLimitedCostMutationVariables,
    FileInput,
} from 'gql/graphql';

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { bimcUpdateCalculationLimitedCostGql } from '../bimCalculationLimitedQueries';
import type { ChapterItem } from '../commonData';
import type { IBimLimitedCostFormData } from '../components/BimCalculationLimitedEditModal';

export interface IUseBimUpdateCalculationLimitedCost {
    calculationId: number;
    selectedChapter: ChapterItem;
    refreshProject?: () => void;
    id?: number;
}

const requestId = 'bimcUpdateCalculationLimitedCost';

export const useBimUpdateCalculationLimitedCost = (
    props: IUseBimUpdateCalculationLimitedCost,
): {
    updateCalculationLimitedCostDataResult: boolean;
    updateCalculationLimitedCostMakeRequest: (
        data: IBimLimitedCostFormData,
        constructionTypesIds: BimcCalculationLimitedCostConstructionTypeInput[],
        formula: string,
        files?: FileInput[],
    ) => void;
    updateCalculationLimitedCostStatus: RequestStatus;
    updateCalculationLimitedCostReset: (value: unknown) => { payload: undefined; type: string } & { meta: Meta };
    bimcUpdateCalculationLimitedCostSetData: any;
} => {
    const { calculationId, selectedChapter, refreshProject, id } = props;

    const onSuccessHandler = useCallback(() => {
        refreshProject?.();
    }, [refreshProject]);

    const dispatch = useDispatch();

    const [
        updateCalculationLimitedCostData,
        updateCalculationLimitedCostStatus,
        ,
        bimcUpdateCalculationLimitedCostRequest,
        bimcUpdateCalculationLimitedCostSetData,
    ] = useRequest({
        requestId,
        query: bimcUpdateCalculationLimitedCostGql,
        withEffect: false,
        dependenciesUpdater: onSuccessHandler,
    });

    const [resetUpdate] = useResetStoreData({ requestId });

    const makeRequest = useCallback(
        (
            data: IBimLimitedCostFormData,
            constructionTypesIds: BimcCalculationLimitedCostConstructionTypeInput[],
            formula: string,
            files?: FileInput[],
        ) => {
            const variables: BimcUpdateCalculationLimitedCostMutationVariables = {
                id: id as number,
                input: {
                    calculationId,
                    name: data.name as string,
                    catalogLimitedCostTypeId: data.limitedCostType?.id,
                    globalLimitedCostId: data.globalLimitedCostId,
                    hasCalculatingParticipation: data?.hasCalculatingParticipation?.id || false,
                    isNorthernAllowance: data.isNorthernAllowance ?? false,
                    isManualLimitsDistribution: data.isManualLimitsDistribution,
                    formula,
                    key: data.key,
                    constructionTypes: constructionTypesIds,
                    chapter: selectedChapter?.id,
                    rationale: data.rationale,
                    file: files?.[0],
                },
            };

            dispatch(bimcUpdateCalculationLimitedCostRequest(variables));
        },
        [id, calculationId, selectedChapter?.id, dispatch, bimcUpdateCalculationLimitedCostRequest],
    );

    return {
        updateCalculationLimitedCostDataResult:
            updateCalculationLimitedCostData?.bimcUpdateCalculationLimitedCost?.result,
        updateCalculationLimitedCostReset: resetUpdate,
        updateCalculationLimitedCostStatus,
        updateCalculationLimitedCostMakeRequest: makeRequest,
        bimcUpdateCalculationLimitedCostSetData,
    };
};
